// v50.144 — useOperativeVerifiedStatus hook.
//
// Single source of truth for the LabourCore Verified status computed
// per operative. Both the OperativeComplianceBadge (display) and the
// parent surfaces that gate transitions (Accept proposal, first
// daily-log submit, invoice creation) call this hook. A module-level
// cache prevents double-fetching when the same operative is referenced
// from multiple places on the same page.

import { useEffect, useState } from "react";
import { LABOURCORE_API } from "@/lib/api";
import { expiryStatus, type ExpiryStatus } from "./certs";
import type { ComplianceDocument } from "@/lib/api/types";

export type VerifiedStatus = "verified" | "warning" | "action" | "declared" | "unknown";

const REQUIRED_CERT_KINDS = ["cscs", "rtw"];

interface DocSummary {
  cert_kind:         string;
  cert_label:        string | null;
  expires_at:        string | null;
  expiryStatus:      ExpiryStatus;
  isDeclarationOnly: boolean;
}

interface VerifiedResult {
  status:        VerifiedStatus;
  docs:          DocSummary[] | null;
  /* Plain-English summary of the gaps that drove the status — used by
     gate confirms ("CSCS is expired; Right to Work is missing"). */
  gapSummary:    string | null;
}

/* In-flight requests deduped by operativeId. Resolved values are
   memoised for 60s — short enough that a fresh upload reflects quickly,
   long enough to keep a single page's renders cheap. */
const cache = new Map<string, { result: VerifiedResult; ts: number }>();
const inflight = new Map<string, Promise<VerifiedResult>>();
const TTL_MS = 60_000;

export function clearVerifiedCacheFor(operativeId: string): void {
  cache.delete(operativeId);
  inflight.delete(operativeId);
}

/* Drop the whole cache on a compliance change event so newly-uploaded
   docs propagate to badges + gates without a hard refresh. */
if (typeof window !== "undefined") {
  try {
    window.addEventListener("lc-compliance-changed", () => {
      cache.clear();
      inflight.clear();
    });
  } catch { /* ignore */ }
}

async function fetchVerifiedStatus(operativeId: string): Promise<VerifiedResult> {
  const data = await LABOURCORE_API.compliance_documents.listForOperative(operativeId);
  const docs: DocSummary[] = ((data as ComplianceDocument[] | null) || [])
    .filter(d => !d.deleted_at)
    .map(d => ({
      cert_kind:         (d.cert_kind || "other").toLowerCase(),
      cert_label:        d.cert_label || null,
      expires_at:        d.expires_at ?? null,
      expiryStatus:      expiryStatus(d.expires_at).status,
      isDeclarationOnly: d.is_declaration_only === true,
    }));

  const gaps: string[] = [];
  let anyMissing = false;
  let anyExpired = false;
  let anyDeclared = false;
  let anyCritical = false;
  let anyWarning = false;

  for (const kind of REQUIRED_CERT_KINDS) {
    const candidates = docs.filter(d => d.cert_kind === kind);
    if (candidates.length === 0) {
      anyMissing = true;
      gaps.push(`${labelFor(kind)} missing`);
      continue;
    }
    const uploaded = candidates.filter(d => !d.isDeclarationOnly);
    if (uploaded.length === 0) {
      anyDeclared = true;
      gaps.push(`${labelFor(kind)} declared but no file uploaded`);
      continue;
    }
    const best = [...uploaded].sort((a, b) => expiryRank(a.expiryStatus) - expiryRank(b.expiryStatus))[0];
    switch (best.expiryStatus) {
      case "expired":  anyExpired  = true; gaps.push(`${labelFor(kind)} expired`); break;
      case "critical": anyCritical = true; gaps.push(`${labelFor(kind)} expires within 7 days`); break;
      case "warning":  anyWarning  = true; gaps.push(`${labelFor(kind)} expires within 30 days`); break;
      default: break;
    }
  }

  let status: VerifiedStatus;
  if (anyMissing || anyExpired)   status = "action";
  else if (anyDeclared)            status = "declared";
  else if (anyCritical || anyWarning) status = "warning";
  else                             status = "verified";

  return { status, docs, gapSummary: gaps.length > 0 ? gaps.join("; ") : null };
}

async function getOrFetch(operativeId: string): Promise<VerifiedResult> {
  const cached = cache.get(operativeId);
  if (cached && Date.now() - cached.ts < TTL_MS) return cached.result;
  const existing = inflight.get(operativeId);
  if (existing) return existing;
  const p = fetchVerifiedStatus(operativeId)
    .then(result => {
      cache.set(operativeId, { result, ts: Date.now() });
      return result;
    })
    .finally(() => { inflight.delete(operativeId); });
  inflight.set(operativeId, p);
  return p;
}

export function useOperativeVerifiedStatus(operativeId: string | null | undefined): VerifiedResult {
  const [state, setState] = useState<VerifiedResult>(() => {
    if (!operativeId) return { status: "unknown" as VerifiedStatus, docs: null, gapSummary: null };
    const cached = cache.get(operativeId);
    if (cached && Date.now() - cached.ts < TTL_MS) return cached.result;
    return { status: "unknown" as VerifiedStatus, docs: null, gapSummary: null };
  });

  useEffect(() => {
    if (!operativeId) { setState({ status: "unknown", docs: null, gapSummary: null }); return; }
    let cancelled = false;
    void (async () => {
      try {
        const result = await getOrFetch(operativeId);
        if (!cancelled) setState(result);
      } catch (e) {
        console.warn("[useOperativeVerifiedStatus]", e);
        if (!cancelled) setState({ status: "unknown", docs: null, gapSummary: null });
      }
    })();
    return () => { cancelled = true; };
  }, [operativeId]);

  return state;
}

/* Imperative single-shot for parent surfaces that need the status at
   the moment of a button click (without subscribing to React state). */
export async function checkOperativeVerifiedStatus(operativeId: string): Promise<VerifiedResult> {
  return getOrFetch(operativeId);
}

/* User-facing copy for a soft gate confirm dialog. Returns null when
   the status doesn't warrant any gate (verified / warning are fine
   to proceed without prompting). */
export function softGateCopy(result: VerifiedResult, action: "accept" | "log" | "invoice"): {
  title:        string;
  message:      string;
  confirmLabel: string;
} | null {
  if (result.status === "verified" || result.status === "warning" || result.status === "unknown") return null;
  const verb =
    action === "accept"  ? "accept this proposal" :
    action === "log"     ? "submit this attendance log" :
                           "raise this invoice";
  const gateNote =
    action === "accept"  ? "Mobilising an operative without valid CSCS + Right to Work breaches site-access rules."
    : action === "log"   ? "Submitting attendance for an operative without valid CSCS + Right to Work breaches site-access rules."
    :                      "LabourCore is moving toward only invoicing for time on site with valid CSCS + Right to Work.";
  return {
    title:        result.status === "action"   ? "Compliance gap on this operative"
                : result.status === "declared" ? "Compliance file not uploaded yet"
                :                                "Compliance check",
    message:      `${result.gapSummary || "Required certs aren't on file."}. ${gateNote} You can still ${verb} for now — we're surfacing this so it can be fixed before site arrival.`,
    confirmLabel: `Proceed and ${verb.split(" ")[0]} anyway`,
  };
}

function expiryRank(s: ExpiryStatus): number {
  switch (s) {
    case "ok":         return 0;
    case "no-expiry":  return 1;
    case "warning":    return 2;
    case "critical":   return 3;
    case "expired":    return 4;
  }
}

function labelFor(kind: string): string {
  if (kind === "cscs") return "CSCS card";
  if (kind === "rtw")  return "Right to Work";
  return kind;
}
